import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ReportService } from '@app/shared/services/report/report.service';
import { ChartConfiguration, ChartData, ChartEvent, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Subscription } from 'rxjs';
import { environment } from '@env/environment';
import { Router } from '@angular/router';
import { periodContainsMonthAndYear } from '@app/shared/utils';

import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'small-sankey-chart',
  templateUrl: './small-sankey-chart.component.html',
  styleUrls: ['./small-sankey-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmallSankeyChartComponent implements OnInit, OnDestroy {
  @Input() trendExecutionSub;
  @Input() isFromHomePage?: boolean = false;

  subscription = new Subscription();
  income;
  net_cashflow;
  expense;
  showZeroState = false;

  month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;
  chartColors: any[] = [
    {
      backgroundColor: 'rgba(82, 91, 235, 1)',
      borderColor: 'rgba(82, 91, 235, 1)',
      pointBackgroundColor: 'rgba(82, 91, 235, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(82, 91, 235,0.8)',
    },
    {
      backgroundColor: 'rgba(250,221,212, 1)',
      borderColor: 'rgba(250,221,212, 1)',
      pointBackgroundColor: 'rgba(250,221,212, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(250,221,212,0.8)',
    },
    {
      backgroundColor: 'rgba(37,80,142, 1)',
      borderColor: 'rgba(37,80,142, 1)',
      pointBackgroundColor: 'rgba(37,80,142, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(37,80,142,0.8)',
    },
  ];
  constructor(private _reportService: ReportService, private _router: Router, private cd: ChangeDetectorRef) {}
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.getChartData();
  }
  getChartData() {
    this.subscription.add(
      this._reportService
        .getReportCashTrends({
          report_execution_id: this.trendExecutionSub.id,
          fromHome: true,
        })
        .subscribe((res) => {
          if (res.length == 0) {
            this.showZeroState = true;
            this.cd.detectChanges();
          }
          let income = res.map((x) => x.income);
          let net_cashflow = res.map((x) => x.net_cashflow);
          let expense = res.map((x) => x.expense * -1);
          let labels = res.map((x) =>
            !periodContainsMonthAndYear(this.trendExecutionSub.period) ? this.month[x.effective_month - 1] : x.effective_month
          );
          this.setMapData(labels, net_cashflow, income, expense);
        })
    );
  }
  setMapData(labels, CashFLow, Income, Expense) {
    this.net_cashflow = CashFLow;
    this.income = Income;
    this.expense = Expense;
    this.barChartData = {
      labels: labels,
      datasets: [
        {
          label: 'Income',
          data: Income,
          stack: 'combined',
          backgroundColor: this.chartColors[0].backgroundColor,
          borderColor: this.chartColors[0].borderColor,
          barPercentage: 0.2,
          borderRadius: 4,
          type: 'bar',
        },
        {
          label: 'Expense',
          data: Expense,
          stack: 'combined',
          backgroundColor: this.chartColors[1].backgroundColor,
          borderColor: this.chartColors[1].borderColor,
          barPercentage: 0.2,
          borderRadius: 4,
          type: 'bar',
        },
        {
          label: 'Net Cashflow',
          data: CashFLow,
          borderColor: this.chartColors[2].borderColor,
          hoverBorderColor: this.chartColors[2].borderColor,
          hoverBackgroundColor: this.chartColors[2].borderColor,
          pointBackgroundColor: this.chartColors[2].pointBackgroundColor,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: this.chartColors[2].pointHoverBorderColor,
        },
      ],
    };
    this.cd.detectChanges();
  }

  public barChartOptions: ChartConfiguration['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    elements: {
      line: {
        tension: 0,
      },
      point: {
        radius: 0,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255,255,255)',
          display: true,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,

          font: {
            size: 12,
            family: "'Archivo', sans-serif",
          },
          color: '#000',
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          display: true,
        },
        ticks: {
          font: {
            size: 12,
            family: "'Archivo', sans-serif",
          },
          color: '#000',
          callback: (value, index, ticks) => {
            let prefixSIgn = '';
            if (+value < 0) {
              prefixSIgn = '-';
              value = +value * -1;
            }
            if (+value < 1000) {
              return prefixSIgn + '$' + value;
            } else if (+value < 1000000) {
              value = +value / 1000;
              return prefixSIgn + '$' + value + 'k';
            }
            if (+value >= 1000000 && +value < 1000000000) {
              value = +value / 1000000;
              return prefixSIgn + '$' + value + 'M';
            } else if (+value >= 1000000000) {
              value = +value / 1000000000;
              return '$' + value + 'B';
            } else {
              return prefixSIgn + '$' + value;
            }
          },
        },
      },
    },
    plugins: {
      tooltip: {
        mode: 'index',
        backgroundColor: '#F3F6FA',
        titleFont: {
          size: 17,
          weight: '700',
        },
        titleColor: '#010B18',
        padding: 16,
        cornerRadius: 4,
        bodyColor: '',
        callbacks: {
          label: (data) => {
            return [data.dataset.label + ' : ' + data.dataset.data[data.dataIndex].toString()];
          },
          title: (data) => {
            return 'Income & Expense Trends';
          },
        },
      },
      legend: {
        display: false,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          color: '#000',
          pointStyle: 'rectRounded',
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'xy',
      intersect: false,
    },
  };
  public barChartType: ChartType = 'line';

  barChartData = {
    labels: [],
    datasets: [],
  };
}
